import { useState, useEffect } from "react";
import {
  Input,
  Slider,
  styled,
  TextareaAutosize,
  Typography,
} from "@mui/material";

const FluxProKontextSettings = ({
  onSettingsChange,
  selectedPrompt,
  generateExact,
}) => {
  const [prompt, setPrompt] = useState(selectedPrompt?.prompt || "");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [numSteps, setNumSteps] = useState(20);
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [seed, setSeed] = useState(generateExact ? selectedPrompt?.seed : "");
  const [safetyTolerance, setSafetyTolerance] = useState(5);

  useEffect(() => {
    // Update settings when selectedPrompt changes
    if (selectedPrompt?.prompt) {
      setPrompt(selectedPrompt?.prompt || "");
    } else {
      setPrompt("");
    }
  }, [selectedPrompt?.prompt]);
  useEffect(() => {
    // Update settings when selectedPrompt changes
    if (generateExact && selectedPrompt?.seed) {
      setSeed(selectedPrompt.seed || "");
    } else {
      setSeed("");
    }
  }, [selectedPrompt?.seed, generateExact]);

  // Notify parent of settings changes
  useEffect(() => {
    if (onSettingsChange) {
      onSettingsChange({
        prompt,
        negativePrompt,
        guidanceScale: guidanceScale,
        numSteps: numSteps,
        aspectRatio,
        seed,
        num_images: 1,
        safetyTolerance: safetyTolerance,
        enable_safety_checker: false,
      });
    }
  }, [
    prompt,
    negativePrompt,
    guidanceScale,
    numSteps,
    aspectRatio,
    seed,
    safetyTolerance,
    onSettingsChange,
  ]);

  return (
    <div className="space-y-4">
      <div>
        <Typography
          htmlFor="prompt"
          className="text-sm font-medium text-gray-200"
        >
          Prompt
        </Typography>
        <TextareaAutosize
          id="prompt"
          placeholder="Describe the image you want to generate..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full p-2 bg-gray-700 border-gray-600 text-white mt-1"
          rows={3}
        />
      </div>

      <div>
        <Typography
          htmlFor="negative-prompt"
          className="text-sm font-medium text-gray-200"
        >
          Negative Prompt (Optional)
        </Typography>
        <TextareaAutosize
          id="negative-prompt"
          placeholder="What you don't want in the image..."
          value={negativePrompt}
          onChange={(e) => setNegativePrompt(e.target.value)}
          className="w-full p-2 bg-gray-700 border-gray-600 text-white mt-1"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Typography className="text-sm font-medium text-gray-200">
            Guidance Scale: {guidanceScale}
          </Typography>
          <Slider
            value={guidanceScale}
            onChange={(e) => setGuidanceScale(e.target.value)}
            aria-label="Default"
            valueLabelDisplay="auto"
            max={20}
            min={0.5}
            step={0.5}
            className="mt-2"
          />
          <p className="text-xs text-gray-400 mt-1">
            Higher values follow prompt more closely
          </p>
        </div>

        <div>
          <Typography className="text-sm font-medium text-gray-200">
            Inference Steps: {numSteps}
          </Typography>
          <Slider
            value={numSteps}
            onChange={(e) => setNumSteps(e.target.value)}
            aria-label="Default"
            valueLabelDisplay="auto"
            max={50}
            min={10}
            step={1}
            className="mt-2"
          />
          <p className="text-xs text-gray-400 mt-1">
            More steps = higher quality
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Typography
            htmlFor="aspect-ratio"
            className="text-sm font-medium text-gray-200"
          >
            Aspect Ratio
          </Typography>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
          >
            <option value="1:1">Square (1:1)</option>
            <option value="16:9">Landscape (16:9)</option>
            <option value="9:16">Portrait (9:16)</option>
            <option value="4:3">Classic (4:3)</option>
            <option value="3:4">Portrait Classic (3:4)</option>
          </select>
        </div>

        <div>
          <Typography
            htmlFor="seed"
            className="text-sm font-medium text-gray-200"
          >
            Seed (Optional)
          </Typography>
          <Input
            id="seed"
            type="number"
            placeholder="Random if empty"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white mt-1"
            inputProps={{ style: { color: "white" } }}
          />
          <p className="text-xs text-gray-400 mt-1">
            Same seed = reproducible results
          </p>
        </div>
      </div>

      <div>
        <Typography className="text-sm font-medium text-gray-200">
          Safety Tolerance: {safetyTolerance}
        </Typography>
        <Slider
          value={safetyTolerance}
          onChange={(e) => setSafetyTolerance(e.target.value)}
          aria-label="Default"
          valueLabelDisplay="auto"
          max={6}
          min={0}
          step={1}
          className="mt-2"
        />
        <p className="text-xs text-gray-400 mt-1">
          Higher values allow more creative content (NSFW disabled)
        </p>
      </div>
    </div>
  );
};

export default FluxProKontextSettings;
